import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";

// Api
import { getEvents } from "../../api/plan";

// Components
import LoadingPlaceholder from "../../components/LoadingPlaceholder";

// Images and mapping
import { imageMap, formatEventDate } from "../MembershipPage/details";
import { photoUrl, photoSrcSet } from "../../utils/photo";


// Card photo: a grey placeholder until the photo arrives, then it fades in
const EventCardImage = ({ evt }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  return (
    <>
      {!isLoaded && <div className="absolute inset-0 bg-surface-200 animate-pulse" aria-hidden="true" />}
      {/* Safari starts downloading as soon as src is set, so the lazy and size hints come first */}
      <img
        loading="lazy"
        decoding="async"
        sizes="(min-width: 1024px) 400px, (min-width: 768px) 50vw, 100vw"
        srcSet={photoSrcSet(evt?.photo, [480, 960])}
        src={photoUrl(evt?.photo, 640) || imageMap[evt?._id]}
        alt={evt.title}
        onLoad={() => setIsLoaded(true)}
        onError={() => setIsLoaded(true)}
        className={`w-full aspect-[4/3] object-cover group-hover:scale-110 transition-[transform,opacity] duration-700 ease-out ${isLoaded ? "opacity-100" : "opacity-0"}`}
      />
    </>
  );
};

const HomePage = () => {
  // loading | ready | failed
  const [status, setStatus] = useState("loading");
  const [eventsData, setEventsData] = useState([]);

  // On a bad connection the request can stall or fail: give up after 20 s,
  // try once more, then offer a retry instead of loading forever
  // Only the latest load may update the page (e.g. "Yenidən yoxla" tapped twice)
  const latestLoad = useRef(0);
  const getData = useCallback(async () => {
    const load = ++latestLoad.current;
    setStatus("loading");
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await getEvents({ timeout: 20000 });
        if (load !== latestLoad.current) return;
        setEventsData(response?.data);
        setStatus("ready");
        return;
      } catch (error) {
        if (load !== latestLoad.current) return;
      }
    }
    setStatus("failed");
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <Layout>
      {/* ========== HERO ========== */}
      <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden bg-surface-950">
        {/* Gradient mesh background */}
        <div className="absolute inset-0 bg-gradient-mesh opacity-30" />

        {/* Floating orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent-600/20 rounded-full blur-[120px] animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-600/20 rounded-full blur-[100px] animate-float-delayed" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[150px]" />

        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-20">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 glass rounded-full px-5 py-2.5 mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-sm font-medium text-white/80">Sadə, sürətli, təhlükəsiz</span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-white leading-[0.95] tracking-tight mb-8">
            Unudulmaz
            <br />
            <span className="text-gradient">anlar</span> sizi
            <br />
            gözləyir
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-surface-400 max-w-2xl mx-auto mb-12 leading-relaxed font-light">
            Konsertlər, festivallar, teatr tamaşaları — sevdiyiniz tədbirlərə bir neçə saniyədə bilet alın.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#events"
              className="group relative inline-flex items-center gap-3 bg-white text-surface-900 font-semibold px-8 py-4 rounded-2xl hover:shadow-[0_20px_60px_-10px_rgba(139,92,246,0.5)] transition-all duration-500 text-base"
            >
              Tədbirlərə bax
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </a>
            <a
              href="tel:+994556611161"
              className="inline-flex items-center gap-2 glass text-white/80 hover:text-white font-medium px-6 py-4 rounded-2xl transition-all duration-300 text-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
              </svg>
              Əlaqə
            </a>
          </div>

          {/* Scroll indicator */}
          <div className="mt-16 md:mt-24 flex flex-col items-center gap-2 text-surface-600">
            <span className="text-xs uppercase tracking-widest">Aşağı sürüşdür</span>
            <div className="w-5 h-8 rounded-full border-2 border-surface-700 flex justify-center pt-1.5">
              <div className="w-1 h-2 bg-surface-500 rounded-full animate-bounce" />
            </div>
          </div>
        </div>
      </section>

      {/* ========== EVENTS ========== */}
      <section id="events" className="relative bg-white">
        {/* Top gradient connector */}
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-surface-950 to-transparent pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-24">
          {/* Section header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-14">
            <div>
              <div className="inline-flex items-center gap-2 text-accent-600 text-sm font-semibold mb-3">
                <div className="w-8 h-px bg-accent-500" />
                AKTIV TƏDBİRLƏR
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-surface-900 tracking-tight">
                Ən yaxın tədbirlər
              </h2>
            </div>
            <p className="text-surface-500 max-w-sm text-base">
              Bakının ən maraqlı tədbirlərinə biletinizi indi sifariş edin
            </p>
          </div>

          {/* Cards grid */}
          {status === "loading" && (
            <div>
              <div className="flex items-center justify-center gap-3 mb-8 text-surface-500 text-sm font-medium" role="status">
                <span className="w-5 h-5 rounded-full border-2 border-accent-200 border-t-accent-600 animate-spin" aria-hidden="true" />
                Tədbirlər yüklənir…
              </div>
              <LoadingPlaceholder />
            </div>
          )}
          {status === "failed" && (
            <div className="text-center py-16 px-6 rounded-3xl bg-surface-50 border border-surface-100">
              <p className="text-surface-900 font-semibold mb-1">Tədbirləri yükləmək alınmadı.</p>
              <p className="text-surface-500 text-sm mb-6">İnternet bağlantınızı yoxlayıb yenidən cəhd edin.</p>
              <button
                type="button"
                onClick={getData}
                className="inline-flex items-center gap-2 bg-surface-900 hover:bg-surface-800 text-white text-sm font-semibold rounded-2xl px-6 py-3 transition"
              >
                Yenidən yoxla
              </button>
            </div>
          )}
          {status === "ready" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {eventsData?.items?.map(evt => (
                <Link
                  key={evt._id}
                  to={`/events/${evt._id}`}
                  className={`group relative rounded-3xl overflow-hidden bg-white border transition-all duration-500 ${evt.isPast ? 'border-surface-100 opacity-60 grayscale pointer-events-none' : 'border-surface-100 hover:border-transparent hover:shadow-[0_25px_60px_-12px_rgba(0,0,0,0.15)]'}`}
                >
                  {/* Gradient border on hover */}
                  {!evt.isPast && <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-accent-400 via-pink-400 to-orange-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10" />}

                  <div className="bg-white rounded-3xl overflow-hidden">
                    {/* Image */}
                    <div className="relative overflow-hidden">
                      <EventCardImage evt={evt} />
                      {evt.isPast && (
                        <div className="absolute top-4 right-4 bg-surface-900/80 text-white text-xs font-semibold px-3 py-1.5 rounded-xl">
                          Keçib
                        </div>
                      )}

                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-surface-950/90 via-surface-950/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />

                      {/* Top badges */}
                      <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
                        <div className="glass rounded-xl px-3.5 py-2 text-white text-xs font-semibold">
                          {formatEventDate(evt?.date)}
                        </div>
                        {evt?.ageLimit && (
                          <div className="glass rounded-xl px-2.5 py-2 text-white text-xs font-bold">
                            {evt?.ageLimit}+
                          </div>
                        )}
                      </div>

                      {/* Bottom info on image */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center gap-1.5 text-white/80 text-sm mb-2">
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.976.544l.062.029.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" clipRule="evenodd" />
                          </svg>
                          {evt?.place}
                        </div>
                        <h3 className="text-xl font-bold text-white leading-snug">
                          {evt.title}
                        </h3>
                      </div>
                    </div>

                    {/* Card footer */}
                    <div className="p-5 flex items-center justify-between">
                      <div className="text-sm text-surface-500 line-clamp-1 flex-1 mr-4" dangerouslySetInnerHTML={{__html: evt?.description}} />
                      <div className="flex-shrink-0 flex items-center gap-2 text-accent-600 font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                        <span>Bilet</span>
                        <div className="w-8 h-8 rounded-full bg-accent-50 group-hover:bg-accent-600 flex items-center justify-center transition-colors duration-300">
                          <svg className="w-4 h-4 text-accent-600 group-hover:text-white transition-colors duration-300" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ========== FEATURES ========== */}
      <section className="relative bg-surface-950 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-mesh opacity-10" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-500/30 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-accent-400 text-sm font-semibold mb-4">
              <div className="w-8 h-px bg-accent-500" />
              NİYƏ TİXY?
              <div className="w-8 h-px bg-accent-500" />
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-4">
              Fərqimiz nədir?
            </h2>
            <p className="text-surface-400 max-w-lg mx-auto text-lg font-light">
              Bilet almaq heç bu qədər asan olmamışdı
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Feature 1 */}
            <div className="group relative glass rounded-3xl p-8 hover:bg-white/[0.12] transition-all duration-500">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500/20 to-accent-600/20 border border-accent-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                <svg className="w-7 h-7 text-accent-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Premium Tədbirlər</h3>
              <p className="text-surface-400 leading-relaxed">
                K-pop, Jazz, Rock, Teatr — Bakının ən eksklüziv tədbirləri bir platformada.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group relative glass rounded-3xl p-8 hover:bg-white/[0.12] transition-all duration-500">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border border-emerald-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                <svg className="w-7 h-7 text-emerald-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Təhlükəsiz Ödəniş</h3>
              <p className="text-surface-400 leading-relaxed">
                256-bit SSL şifrələmə, Apple Pay və Google Pay dəstəyi ilə tam qoruma.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group relative glass rounded-3xl p-8 hover:bg-white/[0.12] transition-all duration-500">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/20 to-amber-600/20 border border-amber-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                <svg className="w-7 h-7 text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Ani Bilet</h3>
              <p className="text-surface-400 leading-relaxed">
                Ödəniş tamamlandıqdan sonra biletiniz dərhal email-ə göndərilir.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '10K+', label: 'Satılmış bilet' },
              { value: '50+', label: 'Tədbir' },
              { value: '99.9%', label: 'Uptime' },
              { value: '24/7', label: 'Dəstək' },
            ].map((stat) => (
              <div key={stat.label} className="text-center py-6">
                <div className="text-3xl md:text-4xl font-extrabold text-gradient mb-2">{stat.value}</div>
                <div className="text-sm text-surface-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
